import type { components } from "../api/schema";

export type NameBasis = components["schemas"]["NameBasis"];

/**
 * How an issuer's name is backed, as words: a label, never a score. Only a
 * domain the issuer proved, or the operator's verification, reads as verified;
 * a demo or unverified name never does.
 */
export function basisLabel(basis: NameBasis | undefined | null, domain?: string | null): string | null {
  switch (basis) {
    case "domain": return domain ? `✓ ${domain}` : "domain-backed";
    case "operator_verified": return "verified by QSeverse";
    case "demo": return "demo issuer";
    case "unverified": return "(name not verified)";
    case "domain_second_person": return "Domain + second person";
    default: return null;
  }
}

/** What each badge proves, for its tooltip (and the site pages). */
export function basisProves(basis: NameBasis, domain?: string | null): string {
  switch (basis) {
    case "domain": return domain ? `The issuer's DID is did:web:${domain}: whoever controls ${domain} controls it. Anyone can check.`
      : "The issuer's DID is the did:web of a domain it controls. Anyone can check.";
    case "operator_verified": return "QSeverse checked that this organisation is who the name says. That is QSeverse's word, not something you can check yourself.";
    case "demo": return "A demo identity for showing the platform. Nobody verified that the name belongs to a real organisation.";
    case "unverified": return "Nothing backs this name: the issuer typed it and nobody checked it.";
    case "domain_second_person": return "An organisation that proved it controls its domain, confirmed by a second person there.";
  }
}

const VERIFIED: ReadonlySet<NameBasis> = new Set(["domain", "operator_verified"]);

export function NameBasisBadge({ basis, domain }: { basis?: NameBasis | null; domain?: string | null }) {
  const label = basisLabel(basis, domain);
  if (!basis || !label) return null;
  return (
    <span className={`basis ${VERIFIED.has(basis) ? "basis-verified" : `basis-${basis === "domain_second_person" ? "light" : basis}`}`}
          title={basisProves(basis, domain)} data-testid="name-basis" data-basis={basis}>{label}</span>
  );
}

/** The badges and what each proves, for /how-it-works and /privacy. */
export function BadgesExplained() {
  const rows: [NameBasis, string, string | undefined][] = [
    ["domain", "✓ <domain>", undefined], ["operator_verified", "verified by QSeverse", undefined],
    ["demo", "demo issuer", undefined], ["unverified", "(name not verified)", undefined]];
  return (
    <div data-testid="badges-explained">
      <p>Next to every issuer's name is how the name is backed. It is a label, not a rating, and checking a claim's signatures never depends on it.</p>
      <ul>
        {rows.map(([b, label]) => <li key={b}><b>{label}</b>: {basisProves(b, b === "domain" ? "<domain>" : undefined)}</li>)}
        <li><b>Domain + second person</b>: {basisProves("domain_second_person")}</li>
      </ul>
    </div>
  );
}
